// frontend/src/components/SplashPage.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api from "../apiClient";

// ── Helpers ──
function timeAgo(ts) {
  if (!ts) return null;
  const d = Date.now() - ts;
  const m = Math.floor(d / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dy = Math.floor(h / 24);
  if (dy === 1) return "Yesterday";
  if (dy < 14) return `${dy}d ago`;
  return null;
}
function readLastUsed(key) {
  try { const v = localStorage.getItem(`ba_lu_${key}`); return v ? parseInt(v, 10) : null; } catch { return null; }
}
function writeLastUsed(key) {
  try { localStorage.setItem(`ba_lu_${key}`, Date.now().toString()); } catch { /* noop */ }
}
function formatDate() {
  return new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

export default function SplashPage({
  email, enterAdmin, enterCarc, enterEua, enterTbs,
  enterBAPB, enterNovs, enterWind, enterOgdb2, enterAW, enterVT, logout,
}) {
  const [apps, setApps] = useState(null);
  const [loading, setLoading] = useState(Boolean(email));
  const [cmdOpen, setCmdOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [tick, setTick] = useState(0); // force re-render for clock
  const [awBadge, setAwBadge] = useState(0); // Agent Watch flagged count
  const [vtData, setVtData] = useState(null); // Value Tracker data
  const [vtVisible, setVtVisible] = useState(false); // feature toggle

  // Live clock (update every 30s)
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let c = false;
    (async () => {
      if (!email) { setApps(null); setLoading(false); return; }
      setLoading(true);
      try {
        const r = await api.get("/apps/visible", { params: { email } });
        if (!c) setApps(Array.isArray(r.data) ? r.data : []);
      } catch { if (!c) setApps([]); }
      finally { if (!c) setLoading(false); }
    })();
    return () => { c = true; };
  }, [email]);

  // Fetch Agent Watch flagged count for badge
  useEffect(() => {
    if (!email) return;
    api.get("/aw/stats").then(r => {
      const f = r.data?.flagged_count || 0;
      setAwBadge(f);
    }).catch(() => {});
  }, [email]);

  // Value Tracker — check if user has access, then fetch data
  useEffect(() => {
    if (!email) return;
    api.get("/apps/access", { params: { email } }).then(r => {
      const rows = Array.isArray(r.data) ? r.data : [];
      const vt = rows.find(row => row.app_key === "value_tracker");
      if (vt && vt.allowed) {
        setVtVisible(true);
        api.get("/cad/value-tracker").then(r2 => setVtData(r2.data)).catch(() => {});
      }
    }).catch(() => {});
  }, [email]);

  const known = useMemo(() => ({
    admin: { title: "Admin Portal",   sub: "Manage users, roles & app access", icon: null, emoji: "\u2699\uFE0F", accent: "#7E9BC9", action: enterAdmin },
    carc:  { title: "CARC",           sub: "Capitol Appraisal Roll Cleaner",   icon: null, emoji: "\uD83E\uDDF9",   accent: "#5B8EC9", action: enterCarc },
    eua:   { title: "E&U Analyzer",   sub: "Real estate BD intelligence",      icon: null, emoji: "\uD83D\uDCCA",   accent: "#7E9BC9", action: enterEua },
    wind:  { title: "Wind Farms",     sub: "Wind farm AV/MW intelligence",     icon: null, emoji: "\uD83C\uDF2C\uFE0F", accent: "#5B8EC9", action: enterWind },
    tbs:   { title: "TBS",            sub: "Multi-county tax bill scraper",    icon: null, emoji: "\uD83D\uDCCB",   accent: "#7E9BC9", action: enterTbs },
    bapb:  { title: "Packet Builder", sub: "Build & manage evidence packets",  icon: null, emoji: "\uD83D\uDCC2",   accent: "#5B8EC9", action: enterBAPB },
    ogdb2: { title: "OGDB2",          sub: "Oil & Gas property database v2",   icon: null, emoji: "\uD83D\uDEE2\uFE0F", accent: "#7E9BC9", action: enterOgdb2 },
    novs:  { title: "NOVS",           sub: "Notice of Value scraper",          icon: null, emoji: "\uD83D\uDCE8",   accent: "#5B8EC9", action: enterNovs },
    aw:    { title: "Agent Watch",    sub: "CAD agent monitoring & AOA forms", icon: null, emoji: "\uD83D\uDD0D",   accent: "#7E9BC9", action: enterAW },
    value_tracker_admin: { title: "Value Tracker", sub: "Configure CAD value release monitoring", icon: null, emoji: "\uD83D\uDCB2", accent: "#5B8EC9", action: enterVT },
  }), [enterAdmin, enterCarc, enterEua, enterTbs, enterBAPB, enterWind, enterNovs, enterOgdb2, enterAW, enterVT]);

  // Keys that control features but aren't launchable apps — hide from tile grid
  const hiddenKeys = useMemo(() => new Set(["value_tracker"]), []);

  const tiles = useMemo(() => {
    if (!Array.isArray(apps)) return [];
    const out = apps.filter(a => !hiddenKeys.has(String(a.key || "").toLowerCase())).map((a) => {
      const key = String(a.key || "").toLowerCase();
      const base = known[key] || { title: a.title || key.toUpperCase(), sub: a.description || "", icon: null, accent: "#7E9BC9", action: () => {} };
      const badge = key === "aw" && awBadge > 0 ? awBadge : null;
      return { key, ...base, lastUsed: readLastUsed(key), badge };
    });
    out.sort((a, b) => (a.key === "admin" ? -1 : b.key === "admin" ? 1 : a.title.localeCompare(b.title)));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apps, known, tick, awBadge]);

  // "Jump back in" — up to 3 most recently used
  const recent = useMemo(() => {
    return tiles.filter(t => t.lastUsed).sort((a, b) => b.lastUsed - a.lastUsed).slice(0, 3);
  }, [tiles]);

  // Launch with tracking
  const launch = useCallback((t) => {
    writeLastUsed(t.key);
    t.action?.();
  }, []);

  // Keyboard shortcuts: 1-9 launches tiles in order
  useEffect(() => {
    const fn = (e) => {
      if (cmdOpen) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setCmdOpen(true); return; }
      const n = parseInt(e.key, 10);
      if (n >= 1 && n <= tiles.length && !e.metaKey && !e.ctrlKey && !e.altKey) {
        // Don't trigger if user is typing in an input
        if (document.activeElement?.tagName === "INPUT") return;
        e.preventDefault();
        launch(tiles[n - 1]);
      }
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [cmdOpen, tiles, launch]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  }, []);
  const firstName = useMemo(() => {
    if (!email) return "";
    const l = email.split("@")[0];
    return l.charAt(0).toUpperCase() + l.slice(1);
  }, [email]);

  const now = useMemo(() => {
    void tick; // dependency
    return new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }, [tick]);

  const dateStr = useMemo(() => formatDate(), []);

  return (
    <div className="d">
      <style>{S}</style>

      {/* ── Header ── */}
      <header className="d-hdr">
        <div className="d-hdr-l">
          <img src="/assets/balogo.png" alt="" className="d-logo" />
          <span className="d-bname">BA Property Tax</span>
        </div>
        <div className="d-hdr-r">
          <button className="d-srch" onClick={() => setCmdOpen(true)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <span className="d-srch-t">Search apps</span>
            <kbd>⌘K</kbd>
          </button>
          <div className="d-usr">
            <div className="d-usr-av">{(email || "?").charAt(0).toUpperCase()}</div>
            <span className="d-usr-n">{email?.split("@")[0]}</span>
          </div>
          <button className="d-out" onClick={logout}>Sign out</button>
        </div>
      </header>

      {/* ── Main ── */}
      <main className="d-main">
        {/* Greeting row */}
        <div className="d-greet d-in" style={{ animationDelay: "0s" }}>
          <div className="d-greet-l">
            <h1 className="d-h1">{greeting}, <strong>{firstName}</strong></h1>
            <p className="d-date">{dateStr} &middot; {now}</p>
          </div>
          <div className="d-stats">
            <div className="d-stat">
              <span className="d-stat-n">{tiles.length}</span>
              <span className="d-stat-l">Apps</span>
            </div>
            <div className="d-stat-div" />
            <div className="d-stat">
              <span className="d-stat-n">{recent.length}</span>
              <span className="d-stat-l">Recent</span>
            </div>
          </div>
        </div>

        {/* Jump back in */}
        {recent.length > 0 && (
          <section className="d-sec d-in" style={{ animationDelay: "0.06s" }}>
            <h2 className="d-sec-h">Jump back in</h2>
            <div className="d-recent">
              {recent.map((t) => (
                <button key={t.key} className="d-rc" onClick={() => launch(t)}
                  style={{ "--ac": t.accent }}
                  onMouseEnter={() => setHovered(t.key)} onMouseLeave={() => setHovered(null)}>
                  <div className="d-rc-ico">
                    {t.emoji ? <span className="d-rc-emoji">{t.emoji}</span> : t.icon ? <img src={t.icon} alt="" className="d-rc-img" /> : <span className="d-rc-ltr">{t.title.charAt(0)}</span>}
                  </div>
                  <div className="d-rc-txt">
                    <span className="d-rc-name">{t.title}</span>
                    <span className="d-rc-time">{timeAgo(t.lastUsed)}</span>
                  </div>
                  <svg className="d-rc-arr" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* All apps */}
        <section className="d-sec d-in" style={{ animationDelay: recent.length > 0 ? "0.12s" : "0.06s" }}>
          <div className="d-sec-row">
            <h2 className="d-sec-h">All apps</h2>
            <span className="d-sec-hint">Press <kbd>1</kbd>–<kbd>{tiles.length}</kbd> to launch</span>
          </div>

          {loading ? (
            <div className="d-grid">
              {[...Array(6)].map((_, i) => <div key={i} className="d-sk d-in" style={{ animationDelay: `${i * 50}ms` }} />)}
            </div>
          ) : (
            <div className="d-grid">
              {tiles.map((t, i) => {
                const ago = timeAgo(t.lastUsed);
                const isH = hovered === t.key;
                const anyH = hovered !== null;
                return (
                  <Tile key={t.key} t={t} i={i} ago={ago} isH={isH} anyH={anyH}
                    launch={launch} setHovered={setHovered} shortcut={i + 1} />
                );
              })}
            </div>
          )}
        </section>

        {/* Value Release Tracker */}
        {vtVisible && vtData && !vtData.pending && (vtData.cads || []).length > 0 && <ValueTracker data={vtData} />}
      </main>

      {/* Footer */}
      <footer className="d-foot">BA Property Tax &middot; {new Date().getFullYear()}</footer>

      {cmdOpen && <Cmd tiles={tiles} onClose={() => setCmdOpen(false)} onGo={(t) => { setCmdOpen(false); launch(t); }} />}
    </div>
  );
}

// ── Value Tracker ──
function ValueTracker({ data }) {
  const released = (data.cads || []).filter(c => c.released);
  const notReleased = (data.cads || []).filter(c => !c.released);
  const allCads = [...released, ...notReleased];
  const tickerItems = [...allCads, ...allCads];

  const interval = data.check_interval_minutes || 60;
  const intervalLabel = interval >= 60 ? `${Math.round(interval / 60)}h` : `${interval}m`;
  const checkedAt = data.checked_at
    ? new Date(data.checked_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : null;

  const fmtRelDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  // Build scrolling headline segments
  const segments = [];
  if (released.length > 0) {
    released.forEach(c => {
      segments.push(`${c.name} released ${data.year} values${c.released_at ? ` \u2014 first detected ${fmtRelDate(c.released_at)}` : ""}`);
    });
  }
  segments.push(`Monitoring ${allCads.length} CADs every ${intervalLabel}`);
  if (checkedAt) segments.push(`Last checked ${checkedAt}`);
  if (notReleased.length > 0) segments.push(`${notReleased.length} CAD${notReleased.length !== 1 ? "s" : ""} still pending`);

  // Repeat enough times to guarantee no gaps on wide screens
  const scrollSegs = [...segments, ...segments, ...segments, ...segments];

  return (
    <section className="d-vt d-in" style={{ animationDelay: "0.18s" }}>
      {/* Scrolling headline */}
      <div className="d-vt-top">
        <div className="d-vt-badge">
          <div className="d-vt-live" />
          <span>LIVE</span>
        </div>
        <div className="d-vt-scroll-wrap">
          <div className="d-vt-scroll" style={{ animationDuration: `${Math.max(segments.length * 8, 20)}s` }}>
            {scrollSegs.map((s, i) => (
              <React.Fragment key={i}>
                <span className="d-vt-scroll-txt">{s}</span>
                <span className="d-vt-scroll-sep" />
              </React.Fragment>
            ))}
          </div>
        </div>
        <span className="d-vt-year">{data.year}</span>
      </div>

      {/* Ticker tape */}
      <div className="d-vt-ticker-wrap">
        <div className="d-vt-ticker">
          {tickerItems.map((c, i) => (
            <div key={`${c.key}-${i}`} className={`d-vt-tick ${c.released ? "d-vt-tick-on" : ""}`}>
              <div className={`d-vt-td ${c.released ? "d-vt-td-on" : ""}`} />
              <span className="d-vt-tn">{c.name}</span>
              {c.released && <span className="d-vt-tcheck">&#10003;</span>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tile ──
function Tile({ t, i, ago, isH, anyH, launch, setHovered, shortcut }) {
  const ref = useRef(null);
  const [m, setM] = useState({ x: 0.5, y: 0.5 });

  const onMove = useCallback((e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    setM({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
  }, []);

  return (
    <button
      ref={ref}
      className={`d-t d-in ${isH ? "d-t-h" : ""} ${anyH && !isH ? "d-t-dim" : ""}`}
      style={{
        animationDelay: `${i * 50}ms`,
        "--ac": t.accent,
        transform: isH
          ? `perspective(600px) rotateX(${(0.5-m.y)*6}deg) rotateY(${(m.x-0.5)*6}deg) translateY(-2px) scale(1.02)`
          : anyH && !isH ? "scale(0.98)" : "none",
      }}
      onClick={() => launch(t)}
      onMouseEnter={() => setHovered(t.key)}
      onMouseMove={onMove}
      onMouseLeave={() => { setHovered(null); setM({ x: 0.5, y: 0.5 }); }}
    >
      {/* Cursor glow */}
      <div className="d-t-glow" style={{
        opacity: isH ? 1 : 0,
        background: `radial-gradient(160px at ${m.x*100}% ${m.y*100}%, ${t.accent}10, transparent 60%)`
      }} />

      {/* Accent bar */}
      <div className="d-t-bar" />

      <div className="d-t-row">
        {/* Icon */}
        <div className="d-t-ico" style={{ position: "relative" }}>
          {t.emoji ? <span className="d-t-emoji">{t.emoji}</span> : t.icon ? <img src={t.icon} alt="" className="d-t-img" /> : <span className="d-t-ltr">{t.title.charAt(0)}</span>}
          {t.badge && <span className="d-t-badge">{t.badge}</span>}
        </div>

        {/* Text */}
        <div className="d-t-txt">
          <span className="d-t-name">{t.title}</span>
          <span className="d-t-sub">{t.sub}</span>
        </div>

        {/* Meta column */}
        <div className="d-t-meta">
          {ago && <span className="d-t-ago">{ago}</span>}
          <kbd className="d-t-key">{shortcut}</kbd>
        </div>

        {/* Arrow */}
        <svg className="d-t-arr" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
      </div>
    </button>
  );
}

// ── Cmd ──
function Cmd({ tiles, onClose, onGo }) {
  const [q, setQ] = useState("");
  const [s, setS] = useState(0);
  const ls = useMemo(() => {
    const f = q.trim().toLowerCase();
    if (!f) return tiles;
    return tiles.filter(t => t.title.toLowerCase().includes(f) || t.sub?.toLowerCase().includes(f));
  }, [tiles, q]);
  useEffect(() => { setS(0); }, [q]);
  useEffect(() => {
    const fn = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowDown") { e.preventDefault(); setS(i => Math.min(i + 1, ls.length - 1)); }
      if (e.key === "ArrowUp") { e.preventDefault(); setS(i => Math.max(i - 1, 0)); }
      if (e.key === "Enter" && ls[s]) { e.preventDefault(); onGo(ls[s]); }
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose, onGo, ls, s]);

  return (
    <div className="d-co" onClick={onClose}>
      <div className="d-cd" onClick={e => e.stopPropagation()}>
        <div className="d-cd-h">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search apps..." className="d-cd-in" />
          <kbd className="d-cd-esc">Esc</kbd>
        </div>
        <div className="d-cd-ls">
          {ls.map((t, i) => (
            <button key={t.key} className={`d-cd-r ${i === s ? "d-cd-a" : ""}`}
              onClick={() => onGo(t)} onMouseEnter={() => setS(i)}>
              <div className="d-cd-ic" style={{ borderLeft: `3px solid ${t.accent}` }}>
                {t.emoji ? <span style={{ fontSize: 16 }}>{t.emoji}</span> : t.icon ? <img src={t.icon} alt="" style={{ width: 18, height: 18, objectFit: "contain" }} /> : <span style={{ fontWeight: 700, color: t.accent, fontSize: 11 }}>{t.title.charAt(0)}</span>}
              </div>
              <div style={{ flex: 1 }}>
                <div className="d-cd-t">{t.title}</div>
                <div className="d-cd-s">{t.sub}</div>
              </div>
              {timeAgo(t.lastUsed) && <span className="d-cd-ago">{timeAgo(t.lastUsed)}</span>}
              <span className="d-cd-ar">→</span>
            </button>
          ))}
          {ls.length === 0 && <div style={{ padding: 24, textAlign: "center", color: "#A0AAC0", fontSize: 13 }}>No results.</div>}
        </div>
      </div>
    </div>
  );
}

// ── Styles ──
const S = `
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}

.d{
  height:100vh;overflow:hidden;
  display:flex;flex-direction:column;
  font-family:'Montserrat',-apple-system,BlinkMacSystemFont,sans-serif;
  background:#F5F6FA;color:#222D50;
}

/* ── Header ── */
.d-hdr{
  flex-shrink:0;display:flex;align-items:center;justify-content:space-between;
  padding:clamp(8px,1.2vh,14px) clamp(16px,3vw,40px);
  background:white;
  border-bottom:1px solid #EEEFF4;
  z-index:10;
}
.d-hdr-l{display:flex;align-items:center;gap:8px;}
.d-logo{height:clamp(22px,2.8vh,28px);width:auto;}
.d-bname{font-size:clamp(11px,1.4vh,14px);font-weight:700;color:#222D50;}
.d-hdr-r{display:flex;align-items:center;gap:clamp(6px,1vw,12px);}
.d-srch{
  display:flex;align-items:center;gap:6px;
  padding:clamp(5px,.7vh,7px) clamp(8px,1vw,14px);border-radius:10px;
  border:1px solid #EEEFF4;background:#F9FAFB;
  color:#8E96AD;cursor:pointer;font-family:inherit;transition:all .2s;
}
.d-srch:hover{border-color:#D8DBE6;background:white;box-shadow:0 2px 8px rgba(126,155,201,.06);}
.d-srch-t{font-size:clamp(10px,1.1vh,12px);font-weight:500;}
.d-srch kbd{font-size:9px;font-weight:600;color:#B8BFD0;font-family:inherit;padding:1px 5px;border-radius:4px;background:#EEEFF4;}
.d-usr{display:flex;align-items:center;gap:6px;padding:3px 10px 3px 3px;border-radius:100px;background:#F9FAFB;border:1px solid #EEEFF4;}
.d-usr-av{width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:#E8EBF2;color:#222D50;font-size:9px;font-weight:700;}
.d-usr-n{font-size:clamp(9px,1vh,11px);color:#8E96AD;font-weight:500;}
.d-out{
  padding:clamp(5px,.7vh,7px) clamp(8px,1vw,12px);border-radius:8px;
  border:1px solid #F5D5D5;background:white;color:#C05050;
  cursor:pointer;font-family:inherit;font-size:clamp(9px,1vh,11px);font-weight:600;transition:all .2s;
}
.d-out:hover{background:#FEF2F2;border-color:#E8B0B0;}

/* ── Main ── */
.d-main{
  flex:1;overflow:hidden;
  display:flex;flex-direction:column;
  padding:clamp(12px,2vh,24px) clamp(24px,4vw,64px);
  width:100%;
  gap:clamp(10px,1.8vh,20px);
}

/* Entrance */
.d-in{animation:d-up .5s cubic-bezier(.175,.885,.32,1.1) both;}
@keyframes d-up{0%{opacity:0;transform:translateY(14px);}100%{opacity:1;transform:translateY(0);}}

/* ── Greeting ── */
.d-greet{display:flex;align-items:flex-end;justify-content:space-between;gap:16px;flex-wrap:wrap;}
.d-greet-l{}
.d-h1{font-size:clamp(16px,2.6vh,26px);font-weight:300;color:#8E96AD;letter-spacing:-.3px;line-height:1.2;}
.d-h1 strong{font-weight:800;color:#222D50;}
.d-date{font-size:clamp(10px,1.2vh,12px);color:#B8BFD0;margin-top:2px;font-weight:500;}

.d-stats{display:flex;align-items:center;gap:clamp(10px,1.5vw,20px);}
.d-stat{display:flex;flex-direction:column;align-items:center;}
.d-stat-n{font-size:clamp(16px,2.2vh,22px);font-weight:800;color:#222D50;line-height:1;}
.d-stat-l{font-size:clamp(8px,.9vh,10px);color:#B8BFD0;font-weight:600;text-transform:uppercase;letter-spacing:.5px;margin-top:1px;}
.d-stat-div{width:1px;height:24px;background:#EEEFF4;}

/* ── Sections ── */
.d-sec{min-height:0;}
.d-sec:last-of-type{flex:1;display:flex;flex-direction:column;min-height:0;}
.d-sec-h{font-size:clamp(10px,1.2vh,13px);font-weight:700;color:#8E96AD;text-transform:uppercase;letter-spacing:.8px;margin-bottom:clamp(6px,1vh,12px);}
.d-sec-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:clamp(6px,1vh,12px);}
.d-sec-hint{font-size:clamp(9px,1vh,11px);color:#C2C5CE;font-weight:500;}
.d-sec-hint kbd{
  display:inline-flex;align-items:center;justify-content:center;
  min-width:16px;height:16px;padding:0 4px;border-radius:4px;
  background:#EEEFF4;font-size:9px;font-weight:700;color:#8E96AD;font-family:inherit;
}

/* ── Recent row ── */
.d-recent{display:flex;gap:clamp(8px,1vw,14px);}
.d-rc{
  flex:1;display:flex;align-items:center;gap:clamp(8px,1vw,12px);
  padding:clamp(10px,1.3vh,16px) clamp(12px,1.5vw,18px);
  border-radius:14px;border:1px solid #EEEFF4;
  background:white;cursor:pointer;font-family:inherit;text-align:left;
  transition:all .3s cubic-bezier(.175,.885,.32,1.1);outline:none;
}
.d-rc:hover{
  border-color:var(--ac,#DDDFE8);
  box-shadow:0 8px 24px rgba(34,45,80,.06);
  transform:translateY(-2px);
}
.d-rc-ico{
  width:clamp(32px,4.5vh,42px);height:clamp(32px,4.5vh,42px);
  border-radius:10px;background:#EEF2FF;border:1px solid #DDE3F7;
  display:flex;align-items:center;justify-content:center;flex-shrink:0;
  transition:all .25s;
}
.d-rc:hover .d-rc-ico{border-color:#B8C8E8;box-shadow:0 0 12px -3px rgba(126,155,201,.3);}
.d-rc-img{width:55%;height:55%;object-fit:contain;}
.d-rc-emoji{font-size:clamp(16px,2.2vh,20px);line-height:1;filter:grayscale(1) brightness(0.55) contrast(1.1);}
.d-rc-ltr{font-size:14px;font-weight:800;color:var(--ac,#7E9BC9);}
.d-rc-txt{flex:1;min-width:0;}
.d-rc-name{display:block;font-size:clamp(10px,1.3vh,13px);font-weight:700;color:#222D50;}
.d-rc-time{display:block;font-size:clamp(9px,1vh,10px);color:#B8BFD0;font-weight:500;margin-top:1px;}
.d-rc-arr{flex-shrink:0;color:#D0D3DC;opacity:0;transform:translateX(-4px);transition:all .25s;}
.d-rc:hover .d-rc-arr{opacity:1;transform:translateX(0);color:var(--ac,#7E9BC9);}

/* ── App grid ── */
.d-grid{
  flex:1;
  display:grid;
  grid-template-columns:repeat(auto-fill,minmax(min(280px,100%),1fr));
  gap:clamp(6px,1vh,12px);
  align-content:start;
  min-height:0;
  overflow-y:auto;
  scrollbar-width:thin;scrollbar-color:#EEEFF4 transparent;
}
.d-grid::-webkit-scrollbar{width:4px;}
.d-grid::-webkit-scrollbar-track{background:transparent;}
.d-grid::-webkit-scrollbar-thumb{background:#EEEFF4;border-radius:4px;}

/* ── Tile ── */
.d-t{
  position:relative;
  border:none;border-radius:14px;
  background:white;
  cursor:pointer;font-family:inherit;text-align:left;outline:none;
  overflow:hidden;
  box-shadow:0 1px 2px rgba(34,45,80,.02);
  border:1px solid #EEEFF4;
  transition:all .3s cubic-bezier(.175,.885,.32,1.1);
  transform-style:preserve-3d;
}
.d-t:hover,.d-t-h{
  border-color:rgba(126,155,201,.1);
  box-shadow:0 8px 28px rgba(34,45,80,.07);
  z-index:3;
}
.d-t-dim{opacity:.4;filter:saturate(.35);transition:all .3s ease;}

/* Bar */
.d-t-bar{
  position:absolute;left:0;top:18%;bottom:18%;width:3px;
  border-radius:0 3px 3px 0;background:var(--ac);
  opacity:0;transform:scaleY(.3);
  transition:all .3s cubic-bezier(.175,.885,.32,1.1);
}
.d-t:hover .d-t-bar,.d-t-h .d-t-bar{opacity:1;transform:scaleY(1);}

/* Glow */
.d-t-glow{position:absolute;inset:0;pointer-events:none;transition:opacity .3s;border-radius:inherit;}

.d-t-row{
  position:relative;z-index:1;
  display:flex;align-items:center;gap:clamp(8px,1vw,14px);
  padding:clamp(10px,1.4vh,16px) clamp(10px,1.2vw,18px);
}

/* Icon */
.d-t-ico{
  width:clamp(34px,4.5vh,44px);height:clamp(34px,4.5vh,44px);
  border-radius:11px;background:#EEF2FF;border:1px solid #DDE3F7;
  display:flex;align-items:center;justify-content:center;flex-shrink:0;
  transition:all .25s;
}
.d-t:hover .d-t-ico{border-color:#B8C8E8;box-shadow:0 0 12px -3px rgba(126,155,201,.3);background:#E8EDFA;}
.d-t-img{width:55%;height:55%;object-fit:contain;}
.d-t-emoji{font-size:clamp(18px,2.4vh,22px);line-height:1;filter:grayscale(1) brightness(0.55) contrast(1.1);}
.d-t-ltr{font-size:clamp(13px,1.6vh,16px);font-weight:800;color:var(--ac,#7E9BC9);}
.d-t-badge{position:absolute;top:-4px;right:-4px;min-width:16px;height:16px;padding:0 4px;border-radius:10px;background:#E05050;color:white;font-size:9px;font-weight:800;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(224,80,80,.4);line-height:1;}

/* Text */
.d-t-txt{flex:1;min-width:0;}
.d-t-name{display:block;font-size:clamp(11px,1.3vh,13px);font-weight:700;color:#222D50;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.d-t-sub{display:block;font-size:clamp(9px,1vh,11px);color:#A0AAC0;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:1px;}
.d-t:hover .d-t-sub{color:#70768D;}

/* Meta */
.d-t-meta{display:flex;flex-direction:column;align-items:flex-end;gap:4px;flex-shrink:0;}
.d-t-ago{font-size:clamp(8px,.9vh,10px);color:#B8BFD0;font-weight:500;white-space:nowrap;}
.d-t-key{
  display:flex;align-items:center;justify-content:center;
  width:clamp(18px,2.2vh,22px);height:clamp(18px,2.2vh,22px);
  border-radius:5px;background:#F5F6FA;border:1px solid #EEEFF4;
  font-size:clamp(9px,1vh,11px);font-weight:700;color:#B8BFD0;font-family:inherit;
  transition:all .25s;
}
.d-t:hover .d-t-key{background:var(--ac);color:white;border-color:var(--ac);box-shadow:0 2px 6px rgba(0,0,0,.1);}

/* Arrow */
.d-t-arr{flex-shrink:0;color:#D0D3DC;opacity:0;transform:translateX(-5px);transition:all .25s;}
.d-t:hover .d-t-arr,.d-t-h .d-t-arr{opacity:1;transform:translateX(0);color:var(--ac,#7E9BC9);}

/* Skeleton */
.d-sk{
  height:clamp(54px,7vh,68px);border-radius:14px;
  background:linear-gradient(90deg,#EEEFF4 25%,#F7F8FC 50%,#EEEFF4 75%);
  background-size:200% 100%;animation:d-up .4s ease both,d-shim 1.3s ease-in-out infinite;
}
@keyframes d-shim{0%{background-position:200% 0;}100%{background-position:-200% 0;}}

/* ── Footer ── */
.d-foot{
  flex-shrink:0;text-align:center;
  padding:clamp(4px,.7vh,8px) 16px;
  font-size:clamp(8px,.85vh,10px);color:#D0D3DC;font-weight:500;
  border-top:1px solid #EEEFF4;
}

/* ── Cmd Palette ── */
.d-co{
  position:fixed;inset:0;z-index:50;
  background:rgba(34,45,80,.08);backdrop-filter:blur(6px);
  display:flex;justify-content:center;padding-top:16vh;
  animation:d-f .12s ease;
}
@keyframes d-f{0%{opacity:0;}100%{opacity:1;}}
.d-cd{
  width:100%;max-width:480px;max-height:55vh;
  display:flex;flex-direction:column;border-radius:18px;
  background:white;border:1px solid #EEEFF4;
  box-shadow:0 20px 60px rgba(34,45,80,.1);overflow:hidden;
  animation:d-ci .2s cubic-bezier(.175,.885,.32,1.1);
}
@keyframes d-ci{0%{opacity:0;transform:translateY(-8px) scale(.97);}100%{opacity:1;transform:translateY(0) scale(1);}}
.d-cd-h{display:flex;align-items:center;gap:10px;padding:14px 18px;border-bottom:1px solid #EEEFF4;color:#A0AAC0;}
.d-cd-in{flex:1;border:none;background:none;color:#222D50;font-size:14px;font-family:inherit;font-weight:500;outline:none;}
.d-cd-in::placeholder{color:#C2C5CE;}
.d-cd-esc{padding:3px 8px;border-radius:5px;background:#F5F6FA;border:1px solid #EEEFF4;color:#A0AAC0;font-size:10px;font-family:inherit;font-weight:600;}
.d-cd-ls{flex:1;overflow-y:auto;padding:6px;scrollbar-width:thin;scrollbar-color:#EEEFF4 transparent;}
.d-cd-r{
  display:flex;align-items:center;gap:12px;
  width:100%;padding:10px 12px;border-radius:12px;
  border:none;background:transparent;cursor:pointer;
  text-align:left;font-family:inherit;transition:all .12s;
}
.d-cd-r:hover,.d-cd-a{background:#F9FAFB;}
.d-cd-ic{width:32px;height:32px;border-radius:9px;background:#F9FAFB;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.d-cd-t{font-size:13px;font-weight:600;color:#222D50;}
.d-cd-s{font-size:10px;color:#A0AAC0;margin-top:1px;}
.d-cd-ago{font-size:9px;color:#B8BFD0;font-weight:500;flex-shrink:0;}
.d-cd-ar{flex-shrink:0;font-size:14px;font-weight:700;color:#7E9BC9;opacity:0;transform:translateX(-4px);transition:all .15s;}
.d-cd-r:hover .d-cd-ar,.d-cd-a .d-cd-ar{opacity:1;transform:translateX(0);}

/* ── Responsive ── */

/* Large desktop (1400px+): 4-col grid */
@media(min-width:1400px){
  .d-grid{grid-template-columns:repeat(4,1fr);}
}
/* Wide desktop (1800px+): 5-col grid */
@media(min-width:1800px){
  .d-grid{grid-template-columns:repeat(5,1fr);}
}

/* Standard desktop (1024-1399): 2 or 3 col auto-fill (already default) */

/* Tablet landscape / small laptop (769-1024) */
@media(max-width:1024px){
  .d-main{padding:clamp(10px,1.5vh,18px) clamp(16px,3vw,32px);gap:clamp(8px,1.4vh,16px);}
  .d-grid{grid-template-columns:repeat(2,1fr);}
  .d-rc{padding:clamp(8px,1vh,12px) clamp(10px,1.2vw,14px);}
}

/* Tablet portrait (601-768) */
@media(max-width:768px){
  .d-usr-n{display:none;}
  .d-bname{display:none;}
  .d-srch-t{display:none;}
  .d-srch kbd{display:none;}
  .d-stats{display:none;}
  .d-main{padding:clamp(8px,1.5vh,16px) clamp(12px,3vw,20px);gap:clamp(8px,1.2vh,14px);}
  .d-grid{grid-template-columns:repeat(2,1fr);gap:clamp(6px,.8vh,10px);}
  .d-recent{flex-direction:column;gap:6px;}
  .d-rc-arr{display:none;}
  .d-h1{font-size:clamp(14px,2.2vh,20px);}
  .d-greet{flex-direction:column;align-items:flex-start;gap:4px;}
  .d-sec-hint{display:none;}
  .d-cd{max-width:92vw;margin:0 auto;}
  .d-co{padding-top:10vh;}
}

/* Large phone (481-600) */
@media(max-width:600px){
  .d-hdr{padding:8px 12px;}
  .d-logo{height:20px;}
  .d-out{padding:5px 8px;font-size:9px;}
  .d-grid{grid-template-columns:repeat(2,1fr);gap:5px;}
  .d-t-row{padding:clamp(8px,1.2vh,12px) clamp(8px,1vw,12px);gap:8px;}
  .d-t-ico{width:30px;height:30px;border-radius:8px;}
  .d-t-arr{display:none;}
  .d-t-meta{gap:2px;}
  .d-t-key{width:16px;height:16px;font-size:8px;border-radius:4px;}
  .d-t-ago{font-size:8px;}
  .d-rc-ico{width:30px;height:30px;border-radius:8px;}
}

/* Small phone (<480) */
@media(max-width:480px){
  .d-main{padding:6px 10px;gap:6px;}
  .d-grid{grid-template-columns:1fr;gap:4px;}
  .d-t-row{padding:10px 12px;gap:10px;}
  .d-t-ico{width:34px;height:34px;}
  .d-t-meta{flex-direction:row;gap:6px;}
  .d-h1{font-size:clamp(13px,2vh,16px);}
  .d-sec-h{font-size:10px;margin-bottom:6px;}
  .d-foot{padding:4px 8px;font-size:8px;}
  .d-rc{padding:8px 10px;border-radius:10px;}
  .d-rc-ico{width:28px;height:28px;}
  .d-cd{border-radius:12px;}
  .d-cd-h{padding:10px 14px;}
  .d-cd-r{padding:8px 10px;}
}

/* Very short screens — collapse recent section to save vertical space */
@media(max-height:600px){
  .d-recent{display:none;}
  .d-main{gap:clamp(6px,1vh,10px);}
  .d-greet{margin-bottom:0;}
  .d-t-row{padding:clamp(6px,1vh,10px) clamp(8px,1vw,14px);}
  .d-t-ico{width:28px;height:28px;border-radius:7px;}
}
@media(max-height:500px){
  .d-greet{display:none;}
  .d-sec-h{margin-bottom:4px;}
}

/* ── Value Tracker ── */
.d-vt{
  flex-shrink:0;
  border-radius:14px;
  background:white;
  border:1px solid #EEEFF4;
  overflow:hidden;
}

/* Scrolling headline bar */
.d-vt-top{
  display:flex;align-items:center;gap:clamp(8px,1vw,12px);
  padding:clamp(7px,.9vh,10px) clamp(12px,1.5vw,18px);
  background:#FAFBFD;
  border-bottom:1px solid #EEEFF4;
}
.d-vt-badge{
  display:flex;align-items:center;gap:5px;
  background:#EEF2FF;
  border:1px solid #DDE3F7;
  padding:3px 9px 3px 7px;border-radius:6px;
  flex-shrink:0;
}
.d-vt-badge span{
  font-size:8px;font-weight:800;letter-spacing:1.2px;
  color:#7E9BC9;text-transform:uppercase;
}
.d-vt-live{
  width:5px;height:5px;border-radius:50%;
  background:#7E9BC9;
  box-shadow:0 0 5px rgba(126,155,201,.4);
  animation:d-vt-pulse 2s ease-in-out infinite;
}
@keyframes d-vt-pulse{
  0%,100%{opacity:1;transform:scale(1);}
  50%{opacity:.3;transform:scale(.7);}
}
.d-vt-scroll-wrap{
  flex:1;overflow:hidden;min-width:0;
  -webkit-mask-image:linear-gradient(90deg,transparent 0%,black 6%,black 94%,transparent 100%);
  mask-image:linear-gradient(90deg,transparent 0%,black 6%,black 94%,transparent 100%);
}
.d-vt-scroll{
  display:flex;align-items:center;white-space:nowrap;
  width:max-content;
  animation:d-vt-crawl 20s linear infinite;
}
@keyframes d-vt-crawl{
  0%{transform:translateX(0);}
  100%{transform:translateX(-25%);}
}
.d-vt-scroll-txt{
  font-size:clamp(9px,1vh,11px);font-weight:600;
  color:#8E96AD;
  padding:0 4px;
}
.d-vt-scroll-sep{
  width:3px;height:3px;border-radius:50%;flex-shrink:0;
  background:#D8DBE6;
  margin:0 10px;
}
.d-vt-year{
  font-size:clamp(9px,1vh,10px);font-weight:800;
  background:#EEF2FF;
  border:1px solid #DDE3F7;
  padding:2px 8px;border-radius:5px;
  color:#7E9BC9;flex-shrink:0;
  letter-spacing:.5px;
}

/* Ticker tape */
.d-vt-ticker-wrap{
  overflow:hidden;
  -webkit-mask-image:linear-gradient(90deg,transparent 0%,black 3%,black 97%,transparent 100%);
  mask-image:linear-gradient(90deg,transparent 0%,black 3%,black 97%,transparent 100%);
}
.d-vt-ticker{
  display:flex;align-items:center;
  padding:clamp(6px,.8vh,9px) 0;
  animation:d-vt-tape 35s linear infinite;
  width:max-content;
}
@keyframes d-vt-tape{
  0%{transform:translateX(0);}
  100%{transform:translateX(-50%);}
}
.d-vt-tick{
  display:flex;align-items:center;gap:5px;
  padding:0 clamp(12px,1.3vw,20px);
  white-space:nowrap;
  border-right:1px solid #F0F1F5;
}
.d-vt-tick:last-child{border-right:none;}
.d-vt-td{
  width:5px;height:5px;border-radius:50%;flex-shrink:0;
  background:#DFE1E8;
}
.d-vt-td-on{
  background:#7E9BC9;
  box-shadow:0 0 4px rgba(126,155,201,.35);
}
.d-vt-tn{
  font-size:clamp(9px,1vh,11px);font-weight:600;
  color:#C2C5CE;
}
.d-vt-tick-on .d-vt-tn{
  color:#222D50;
}
.d-vt-tcheck{
  font-size:9px;font-weight:800;
  color:#7E9BC9;
}

@media(max-width:768px){
  .d-vt-ticker{animation-duration:24s;}
  .d-vt-scroll{animation-duration:16s !important;}
}
@media(max-height:600px){
  .d-vt{display:none;}
}
`;
